import initNewRelic from '../../analytics/newrelic';
import { NAV_CUSTOM_EVENTS, REMINDER_DELAY } from '../../constants';

/**
 * Confirm optional props are configured as expected.
 * @throws Error
 * @returns {Boolean}
 */
function validateOpts({ onSearchSubmit }) {
  if (onSearchSubmit && typeof onSearchSubmit !== 'function') {
    throw new Error('onSearchSubmit config expected to be a function that takes a search string.');
  }
  return true;
}

/**
 * Singleton for interacting with the Dotcom nav components.
 * Guillotine will invoke methods in this class to initialize and register configurations.
 * @example
 * window.NikeDotcomNav.mount(options)
 * window.NikeDotcomNav.api.setCartCount(5);
 */
export default class NikeDotcomNav {
  /**
   * The environment (STAGE) this nav was generated in.
   * @type {String} prod|test|dev
   */
  env = 'prod';

  /**
   * Index of named components that have public methods.
   * @type {Object}
   */
  components = {};
  /**
   * For mocking.
   * @type {Console}
   */
  logger = console;
  /**
   * Flag indicating if the configurations have been mounted yet.
   * @type {Boolean}
   */
  mounted = false;
  /**
   * The shared redux store.
   * @type {null|Store}
   */
  store = null;
  /**
   * Localization messages.
   * @type {{}}
   */
  messages = {};
  /**
   * Keymap of configurations being used.
   * @type {Object}
   */
  configurations = {};
  /**
   * Metadata about the generated content.
   * @see configurations/shared.js
   * @type {null|Object}
   */
  generation = null;
  /**
   * Initial redux state of the generated components.
   * @type {{}}
   */
  initialState = {};
  /**
   * Events to call when instance loads.
   * @type {*[]}
   */
  onLoadEvents = [];
  /**
   * Public API methods.
   * @type {{}}
   */
  // api = createPublicApiMethods(this);
  /**
   * The default analytics client.
   * @type {Object}
   */
  analyticsClient = null;
  /**
   * Expose the SQ cookie controls.
   * @type {Object}
   */
  // SQCookie = SQCookie;
  /**
   * Registered action listeners.
   * @type {{}}
   */
  actionListeners = {};
  /**
   * New relic instance.
   * @type {null|Object}
   */
  newrelic = initNewRelic(this);
  /**
   * Default mount options.
   */
  opts = {
    /**
     * Keymap of api calls that can be overwritten.
     * @type {Object}
     */
    calls: {},
    /**
     * Keymap of link URLs that can trigger actions when clicked.
     * @type {Object}
     */
    triggers: {},
    /**
     * Milliseconds to show privacy policy if enabled.
     * Leave at 0 to disable.
     * @type {Number}
     */
    showPrivacyPolicy: 0,
    /**
     * Shows the geomismatch dialog if conditions are met.
     * Set to true to enable.
     * @type {Boolean}
     */
    showGeomismatch: false,
    /**
     * Customize the geomismatch redirect
     * @type {Function | null}
     * @param country {String | null}
     * @param language {String | null}
     * @returns {Object | null}
     */
    redirectGeomismatch: null,
    /**
     * Sets legacy country, language, and locale cookies in client.
     * @type {Boolean}
     */
    setLegacyCookies: false,
    /**
     * Optional function to override the default search endpoint.
     * This function MUST call its own search API with the provided query.
     * @type {Function}
     * @param {String} query
     */
    onSearchSubmit: null,
    /**
     * Use user geo/IP to determine whether to show privacy banner?
     * Set to false to use the nav geo to determine this.
     * @type {Boolean}
     */
    useGeoPrivacy: true,
    /**
     * Enables or disables the nav "Peak-a-boo" scrolling behavior.
     * @type {Boolean|null} false causes nav to be fixed.
     */
    peakabooNavEnabled: null,
  };

  /**
   * Deprecated alias to components index.
   * @deprecated
   * @returns {{objects: {}}}
   */
  get vanilla() {
    // Don't clutter the console in prod
    if (this.env !== 'prod') {
      this.logger.warn('NikeDotcomNav.vanilla.objects is deprecated. Use NikeDotcomNav.components');
    }
    return { objects: this.components };
  }

  /**
   * Called when the nav is booted for the first time.
   * Includes options brought over during pre-generation.
   * @see configurations/shared.js
   * @param opts {Object}
   */
  init(opts) {
    this.logger.info('NikeDotcomNav: Initializing.');
    const {
      country,
      language,
      locale,
      currency,
      clientVersion,
      clientBaseUrl,
      initialState,
      messages,
      metadata: { generationId, timestamp },
      env = 'prod',
    } = opts;

    this.env = env;
    this.initialState = initialState;
    this.messages = messages;
    this.locale = {
      country,
      language,
      currency,
      ...locale,
      get locale() {
        this.logger.warn('Deprecated: NikeDotcomNav.locale.locale');
        return locale;
      },
    };
    this.generation = {
      country,
      language,
      id: `${country}/${language}/${generationId}`,
      timestamp: new Date(timestamp),
      version: clientVersion,
      baseUrl: clientBaseUrl,
    };

    this.newrelic = initNewRelic(this, opts);
    this.newrelic.initPerformanceMetrics();

    // A friendly reminder to mount if you haven't already.
    window.setTimeout(() => {
      // istanbul ignore next
      if (!this.mounted) {
        this.logger.warn('NikeDotcomNav.mount() not called yet?');
      }
    }, REMINDER_DELAY);
  }

  /**
   * Mounts vanilla javascript components and create interactivity.
   * Should be called from the consuming application when ready.
   * @param opts {Object} optional props to pass to all components
   * @param container {HTMLElement}
   * @returns {Boolean}
   */
  mount(opts = {}) {
    this.logger.info('NikeDotcomNav: Mounting.');
    try {
      if (this.mounted) {
        this.logger.warn('NikeDotcomNav already mounted');
        return true;
      }

      validateOpts(Object.assign(this.opts, opts));

      this.mounted = true;
      window.dispatchEvent(new Event(NAV_CUSTOM_EVENTS.MOUNTED));
      this.unloadEvents();
    } catch (err) {
      this.newrelic.error(err, 'NikeDotcomNav:mount');
      throw err;
    }
    return true;
  }
  /**
   * Register an onload event to call when done mounting.
   * @throws Error
   * @param callback
   * @returns void
   */
  onLoad(callback) {
    if (typeof callback !== 'function') {
      throw new TypeError('Argument must be a callback function');
    }
    if (this.mounted) {
      callback();
    } else {
      this.onLoadEvents.push(callback);
    }
  }

  /**
   * Unload and call all registered load events.
   * @returns void
   */
  unloadEvents() {
    while (this.onLoadEvents.length) this.onLoadEvents.pop()();
  }

  /**
   * Register a new configuration.
   * @param configuration {String} commerce-header|brand-header|footer...
   * @param metadata {Object}
   */
  register(configuration, metadata) {
    this.configurations[configuration] = metadata;
  }
}
